import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';

import * as ProjectsV2Store from '../../../../redux/projectsV2';
import ProjectsTableBody from './ProjectsTableBody';
import ProjectsTableHeader from './ProjectsTableHeader';
import LoadingProjectsTableBody from './LoadingProjectsTableBody';
import AddProjectsCTA from '../../shared/CTAs/AddProjectsCTA';
import ProjectsBulkSelectionBanner from './ProjectBulkSelectionBanner';
import AddReleaseGroupCTA from '../../shared/CTAs/AddReleaseGroupCTA';
import { isProjects } from '../../../../../shared/projectsV2';
import FallbackImage from '../../../generic/FallbackImage';

const ProjectsTable: VoidFunctionComponent = () => {
  const { loading: isLoading, errors } = useSelector(ProjectsV2Store.selectors.getAsyncStatusFetchProjects);
  const category = useSelector(ProjectsV2Store.selectors.getCategory);
  const projects = useSelector(ProjectsV2Store.selectors.getProjects);
  const pageSize = useSelector(ProjectsV2Store.selectors.getCount);
  const hasFilterApplied = useSelector(ProjectsV2Store.selectors.getHasFilterApplied);

  const renderProjectsTableBody = () => {
    // show loading rows animation if waiting for backend query
    if (isLoading) {
      return <LoadingProjectsTableBody count={pageSize} />;
    }

    // show error state if errors exist
    if (!_.isEmpty(errors)) {
      return (
        <FallbackImage
          src="error-plug"
          alt="Network error"
          hasPadding
        >
          <div>Oops! There was an error.</div>
          <div>Please try again in a bit.</div>
        </FallbackImage>
      );
    }

    // display a fallback if the given search / filters return no projects OR an org has no projects yet
    if (_.isEmpty(projects)) {
      // if the user has entered a title search / filter, show them a "no results found" style message
      if (hasFilterApplied) {
        return (
          <FallbackImage
            src="no-results"
            alt="No projects found"
            hasPadding
          >
            We couldn&apos;t find any results.
          </FallbackImage>
        );
      }

      // show CTA page for adding a new project / release group
      return isProjects(category) ? <AddProjectsCTA /> : <AddReleaseGroupCTA />;
    }

    return <ProjectsTableBody category={category} projects={projects} />;
  };

  return (
    <div className="projects-table">
      <ProjectsTableHeader />
      <ProjectsBulkSelectionBanner />
      {renderProjectsTableBody()}
    </div>
  );
};

export default ProjectsTable;
